import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Mail, Phone, PhoneForwarded, Linkedin, Building2, Info, ExternalLink, Copy, CheckCircle2, GraduationCap } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from '@/components/utils/translations';
import PersonAvatar from '@/components/PersonAvatar';
import { createPageUrl } from '@/utils';
import { notify } from '@/components/ui/notify';
import { PersonFinal } from '@/api/entities';
import { getCayuseTargetProductListForContext } from '@/lib/accountTargetProducts';
import { personaFitBand } from '@/lib/personaFitLabel';

const PERSONA_STYLES = {
    strategic: { color: 'bg-green-100 text-green-800', textColor: 'text-green-800' },
    supportive: { color: 'bg-blue-100 text-blue-800', textColor: 'text-blue-800' },
    passive: { color: 'bg-gray-100 text-gray-800', textColor: 'text-gray-800' },
    lowFit: { color: 'bg-red-100 text-red-800', textColor: 'text-red-800' },
};
const getPersonaLabel = (score, t) => {
    const band = personaFitBand(score) ?? 'passive';
    return { label: t(band), ...PERSONA_STYLES[band] };
};

export default function ProfileHeader({ person, account, accountUrl: providedAccountUrl, onPersonUpdate, tenantId, almaMaterMatch }) {
    const { t } = useTranslation();
    const [copiedEmail, setCopiedEmail] = useState(false);
    const [copiedPhone, setCopiedPhone] = useState(false);
    const [copiedSwitchboard, setCopiedSwitchboard] = useState(false);
    const [editingProductAffinity, setEditingProductAffinity] = useState(false);
    const [savingProductAffinity, setSavingProductAffinity] = useState(false);
    const personaLabel = getPersonaLabel(person?.persona_fit_score || 0, t);
    
    const handleCopyEmail = async () => {
      if (person?.email) {
        try {
          await navigator.clipboard.writeText(person.email);
          setCopiedEmail(true);
          setTimeout(() => setCopiedEmail(false), 2000);
          notify({
            title: 'Copied to clipboard',
            description: `Email address copied: ${person.email}`,
          });
        } catch (error) {
          console.error('Failed to copy email:', error);
          notify({
            title: 'Copy failed',
            description: 'Please try again',
            variant: 'destructive'
          });
        }
      }
    };

    const handleCopyPhone = async () => {
      if (person?.phone) {
        try {
          await navigator.clipboard.writeText(person.phone);
          setCopiedPhone(true);
          setTimeout(() => setCopiedPhone(false), 2000);
          notify({
            title: 'Copied to clipboard',
            description: `Phone number copied: ${person.phone}`,
          });
        } catch (error) {
          console.error('Failed to copy phone:', error);
          notify({
            title: 'Copy failed',
            description: 'Please try again',
            variant: 'destructive'
          });
        }
      }
    };

    const switchboardNumber =
      person?.switchboard != null && String(person.switchboard).trim() !== ''
        ? String(person.switchboard).trim()
        : '';

    const handleCopySwitchboard = async () => {
      if (!switchboardNumber) return;
      try {
        await navigator.clipboard.writeText(switchboardNumber);
        setCopiedSwitchboard(true);
        setTimeout(() => setCopiedSwitchboard(false), 2000);
        notify({
          title: 'Copied to clipboard',
          description: `Switchboard number copied: ${switchboardNumber}`,
        });
      } catch (error) {
        console.error('Failed to copy switchboard:', error);
        notify({
          title: 'Copy failed',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
    };
    
    // Create account URL if not provided - try account.id (MongoDB ObjectId) or account.account_id
    const isValidAccountId = (id) => {
      if (!id) return false;
      // MongoDB ObjectId: 24 hex characters
      return /^[a-f0-9]{24}$/i.test(id);
    };
    
    const tid = String(tenantId || account?.tenant_id || '').toLowerCase();
    const productOptions =
      tid === 'cayuse'
        ? getCayuseTargetProductListForContext(account?.target_products)
        : Array.isArray(account?.target_products)
          ? account.target_products.map((p) => String(p).trim()).filter(Boolean)
          : typeof account?.target_products === 'string' && account.target_products.trim()
            ? [account.target_products.trim()]
            : [];
    const showProductAffinityUi = tid === 'learnwise' || tid === 'cayuse';
    const handleProductAffinityChange = async (value) => {
        if (!person?.id || !onPersonUpdate) return;
        setSavingProductAffinity(true);
        try {
            const updated = await PersonFinal.update(person.id, { product_affinity: (value || '').trim() || null });
            onPersonUpdate(updated);
            setEditingProductAffinity(false);
        } catch (err) {
            console.error('Error saving product affinity:', err);
        } finally {
            setSavingProductAffinity(false);
        }
    };

    const accountUrl = providedAccountUrl || (account && (
      (account.id && isValidAccountId(account.id))
        ? createPageUrl(`AccountFinal?id=${account.id}`)
        : (account.account_id)
          ? createPageUrl(`AccountFinal?id=${encodeURIComponent(account.account_id)}`)
          : null
    ));

    if (!person) {
        return (
            <Card className="border-0 shadow-md">
                <CardContent className="p-4 md:p-6">
                    <p className="text-center text-gray-500">Loading person data...</p>
                </CardContent>
            </Card>
        );
    }

    console.log('👤 ProfileHeader persona data:', {
        name: `${person.first_name} ${person.last_name}`,
        fit_score: person.persona_fit_score,
        has_rationale: !!person.persona_score_rationale,
        rationale_length: person.persona_score_rationale?.length || 0
    });

    return (
        <Card className="border-0 shadow-md">
            <CardContent className="p-4 md:p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center gap-3 md:gap-4 flex-grow min-w-0">
                        <TooltipProvider delayDuration={0}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className={person.person_validation_url ? 'cursor-help' : ''}>
                                        <PersonAvatar
                                            personId={person.person_id}
                                            hostedPhotoUrl={person.hosted_photo_url}
                                            linkedinPhotoUrl={person.profile_photo_url}
                                            firstName={person.first_name}
                                            lastName={person.last_name}
                                            size="xl"
                                            className="md:w-16 md:h-16"
                                        />
                                    </div>
                                </TooltipTrigger>
                                {person.person_validation_url && (
                                    <TooltipContent 
                                        side="bottom" 
                                        align="start"
                                        className="bg-[#0A1A2F] text-white border-0 shadow-lg p-3 max-w-[400px]"
                                    >
                                        <div className="space-y-2">
                                            <div className="flex items-start gap-2">
                                                <ExternalLink className="w-4 h-4 text-[#00C2C2] mt-0.5 flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs font-semibold text-[#00C2C2] mb-1">Validation Source</p>
                                                    <a 
                                                        href={person.person_validation_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-xs text-white hover:text-[#00C2C2] break-all"
                                                    >
                                                        {person.person_validation_url}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </TooltipContent>
                                )}
                            </Tooltip>
                        </TooltipProvider>
                        
                        <div className="flex-grow min-w-0">
                            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
                                <h1 className="text-lg md:text-3xl font-bold text-[#0A1A2F] break-words">
                                    {person.first_name} {person.last_name}
                                </h1>
                                
                                {person.persona_fit_score != null && (
                                    <Badge className={`${personaLabel.color} text-xs px-2 py-1 flex-shrink-0 md:hidden`}>
                                        {personaLabel.label} ({person.persona_fit_score || 0})
                                    </Badge>
                                )}
                            </div>
                            
                            <p className="text-sm md:text-base text-[#5C6B7A] mt-1 break-words">{person.role_title}</p>
                            
                            {/* Show account name with link if accountUrl is available */}
                            {(account?.account_name || person?.account_name) && (
                              accountUrl ? (
                                <Link to={accountUrl} className="text-xs md:text-sm text-[#00C2C2] hover:underline flex items-center gap-1 mt-1">
                                  <Building2 className="w-3 h-3 md:w-4 md:h-4" />
                                  {account?.account_name || person?.account_name}
                                </Link>
                              ) : (
                                <div className="text-xs md:text-sm text-[#5C6B7A] flex items-center gap-1 mt-1">
                                  <Building2 className="w-3 h-3 md:w-4 md:h-4" />
                                  {account?.account_name || person?.account_name}
                                </div>
                              )
                            )}
                        </div>
                    </div>

                    {person.persona_fit_score != null && (
                        <div className="hidden md:flex flex-col items-center gap-1 flex-shrink-0">
                            <TooltipProvider delayDuration={0}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className={`w-16 h-16 ${personaLabel.color} rounded-full flex items-center justify-center font-bold text-xl cursor-help transition-all hover:scale-105 hover:shadow-md`}>
                                            {person.persona_fit_score || 0}
                                        </div>
                                    </TooltipTrigger>
                                    {person.persona_score_rationale && (
                                        <TooltipContent 
                                            side="top" 
                                            align="center"
                                            sideOffset={8}
                                            className="w-[90vw] max-w-[600px] md:w-[600px] bg-[#0A1A2F] text-white border-0 shadow-lg p-4 z-[9999]"
                                        >
                                            <p className="text-sm font-semibold mb-2 text-[#00C2C2]">Persona Fit Rationale</p>
                                            <p className="text-sm text-white leading-relaxed">{person.persona_score_rationale}</p>
                                        </TooltipContent>
                                    )}
                                </Tooltip>
                            </TooltipProvider>
                            <span className={`text-xs font-semibold ${personaLabel.textColor}`}>{personaLabel.label}</span>
                        </div>
                    )}
                </div>

                <div className="mt-4 space-y-3">
                    <div className="flex flex-wrap items-center gap-1.5 md:gap-2 text-xs md:text-sm text-[#5C6B7A]">
                        {person.department && (
                            <Badge variant="outline" className="text-xs">{t('department')}: {person.department}</Badge>
                        )}
                        {person.country && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 text-xs">
                                {t('country')}: {person.country}
                            </Badge>
                        )}
                        {person.social_presence_score != null && (
                            <TooltipProvider delayDuration={0}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className="inline-block cursor-help">
                                            <Badge variant="outline" className="bg-purple-50 text-purple-800 border-purple-200 text-xs">
                                                Social Presence: {person.social_presence_score}
                                            </Badge>
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent 
                                        side="top" 
                                        align="center"
                                        className="w-[90vw] max-w-[400px] md:w-[400px] bg-[#0A1A2F] text-white border-0 shadow-lg p-4 z-[9999]"
                                    >
                                        <p className="text-sm font-semibold mb-2 text-[#00C2C2]">Social Presence Score</p>
                                        <p className="text-sm text-white leading-relaxed">
                                            An indicator of a persona's overall visibility and activity on LinkedIn. Higher scores suggest they're more likely to respond and engage with sales outreach on the platform.
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                        {person.worked_for_customer && (
                            <Badge className="bg-blue-500 text-white text-xs">
                                Former Client {person.client_name && `(${person.client_name})`}
                            </Badge>
                        )}
                        {almaMaterMatch && (
                            <Badge className="bg-teal-50 text-teal-800 border border-teal-200 text-xs gap-1">
                                <GraduationCap className="w-3 h-3" />
                                {almaMaterMatch.matched_via === 'employer' ? 'Tie to' : 'Shared alma mater'}: {almaMaterMatch.matched_university}
                            </Badge>
                        )}
                        {showProductAffinityUi && (editingProductAffinity ? (
                            <div className="flex items-center gap-2">
                                {productOptions.length > 0 ? (
                                    <Select
                                        value={person.product_affinity || '__none__'}
                                        onValueChange={(v) => handleProductAffinityChange(v === '__none__' ? '' : v)}
                                        disabled={savingProductAffinity}
                                    >
                                        <SelectTrigger className="h-7 w-[140px] text-xs">
                                            <SelectValue placeholder="Product affinity" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">(None)</SelectItem>
                                            {productOptions.map(p => (
                                                <SelectItem key={p} value={p}>{p}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    <Input
                                        className="h-7 w-[140px] text-xs"
                                        placeholder="e.g. SYS, EXAM"
                                        defaultValue={person.product_affinity || ''}
                                        onBlur={(e) => {
                                            const v = e.target.value?.trim();
                                            if (v !== (person.product_affinity || '')) handleProductAffinityChange(v);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleProductAffinityChange(e.target.value);
                                            }
                                        }}
                                    />
                                )}
                                <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setEditingProductAffinity(false)}>Cancel</Button>
                            </div>
                        ) : (person.product_affinity ? (
                            <Badge
                                variant="outline"
                                className="bg-[#00C2C2]/10 text-[#00A3A3] border-[#00C2C2]/30 text-xs cursor-pointer hover:bg-[#00C2C2]/20"
                                onClick={() => onPersonUpdate && setEditingProductAffinity(true)}
                            >
                                {person.product_affinity}
                            </Badge>
                        ) : onPersonUpdate && (
                            <Button variant="ghost" size="sm" className="h-6 text-xs text-[#5C6B7A]" onClick={() => setEditingProductAffinity(true)}>
                                + Add product affinity
                            </Button>
                        )))}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-[#5C6B7A]">
                        {person.email && (
                            <div className="flex items-center gap-1.5">
                                <a href={`mailto:${person.email}`} className="flex items-center gap-1 hover:text-[#00C2C2]">
                                    <Mail className="w-3 h-3 md:w-4 md:h-4" /> {person.email}
                                </a>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 w-5 p-0 hover:bg-gray-100"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleCopyEmail();
                                    }}
                                    title="Copy email"
                                >
                                    {copiedEmail ? (
                                        <CheckCircle2 className="w-3 h-3 text-green-600" />
                                    ) : (
                                        <Copy className="w-3 h-3" />
                                    )}
                                </Button>
                            </div>
                        )}
                        {(person.phone || switchboardNumber) && (
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                {person.phone && (
                                    <TooltipProvider delayDuration={0}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div className="flex items-center gap-1.5">
                                                    <a href={`tel:${person.phone}`} className="flex items-center gap-1 hover:text-[#00C2C2]">
                                                        <Phone className="w-3 h-3 md:w-4 md:h-4 shrink-0" /> {person.phone}
                                                    </a>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-5 w-5 p-0 hover:bg-gray-100"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleCopyPhone();
                                                        }}
                                                        title="Copy phone"
                                                    >
                                                        {copiedPhone ? (
                                                            <CheckCircle2 className="w-3 h-3 text-green-600" />
                                                        ) : (
                                                            <Copy className="w-3 h-3" />
                                                        )}
                                                    </Button>
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent
                                                side="top"
                                                align="start"
                                                className="bg-[#0A1A2F] text-white border-0 shadow-lg z-[9999]"
                                            >
                                                Direct or department number
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                                {switchboardNumber && (
                                    <TooltipProvider delayDuration={0}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div className="flex items-center gap-1.5">
                                                    <a href={`tel:${switchboardNumber}`} className="flex items-center gap-1 hover:text-[#00C2C2]">
                                                        <PhoneForwarded className="w-3 h-3 md:w-4 md:h-4 shrink-0" /> {switchboardNumber}
                                                    </a>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-5 w-5 p-0 hover:bg-gray-100"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleCopySwitchboard();
                                                        }}
                                                        title="Copy switchboard"
                                                    >
                                                        {copiedSwitchboard ? (
                                                            <CheckCircle2 className="w-3 h-3 text-green-600" />
                                                        ) : (
                                                            <Copy className="w-3 h-3" />
                                                        )}
                                                    </Button>
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent
                                                side="top"
                                                align="start"
                                                className="bg-[#0A1A2F] text-white border-0 shadow-lg z-[9999]"
                                            >
                                                Switchboard number
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                            </div>
                        )}
                        {person.linkedin_url && (
                            <a href={person.linkedin_url} target="_blank" rel="noopener noreferrer">
                                <Button variant="outline" size="sm" className="h-6 md:h-7 px-2 py-1 text-xs">
                                    <Linkedin className="w-3 h-3 mr-1" /> LinkedIn
                                </Button>
                            </a>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}